(() => {
    const selector = '[data-ttd-presentation]';

    class TTDPresentation {
      constructor(root) {
        this.root = root;
        this.stage = root.querySelector('[data-presentation-stage]');
        this.slides = [...root.querySelectorAll('[data-presentation-slide]')];
        this.progressButtons = [...root.querySelectorAll('[data-presentation-go-to]')];
        this.previousButton = root.querySelector('[data-presentation-previous]');
        this.nextButton = root.querySelector('[data-presentation-next]');
        this.pauseButton = root.querySelector('[data-presentation-pause]');
        this.restartButton = root.querySelector('[data-presentation-restart]');
        this.fullscreenButton = root.querySelector('[data-presentation-fullscreen]');
        this.autoplayButton = null;
        this.currentNumber = root.querySelector('[data-current-number]');
        this.currentLabel = root.querySelector('[data-current-label]');
        this.status = root.querySelector('[data-presentation-status]');
        this.index = 0;
        this.locked = false;
        this.paused = false;
        this.pointerStart = null;
        this.wheelTotal = 0;
        this.wheelTimer = null;
        this.uiTimer = null;
        this.autoplayTimer = null;
        this.autoplaying = false;
        this.autoplayDelay = 15000;
        this.listeners = [];
        this.init();
      }

      init() {
        document.body.classList.add('ttd-presentation-mode');
        if (!this.stage || this.slides.length === 0) return;

        this.autoplayButton = this.buildAutoplayButton();

        this.bind(this.previousButton, 'click', () => this.goTo(this.index - 1));
        this.bind(this.nextButton, 'click', () => this.goTo(this.index + 1));
        this.bind(this.pauseButton, 'click', () => this.togglePause());
        this.bind(this.autoplayButton, 'click', () => this.toggleAutoplay());
        this.bind(this.restartButton, 'click', () => this.restart());
        this.bind(this.fullscreenButton, 'click', () => this.toggleFullscreen());

        this.progressButtons.forEach((button) => {
          this.bind(button, 'click', () => this.goTo(Number(button.dataset.presentationGoTo)));
        });

        this.bind(document, 'keydown', (event) => this.onKeydown(event));
        this.bind(this.root, 'wheel', (event) => this.onWheel(event), { passive: false });
        this.bind(this.stage, 'pointerdown', (event) => this.onPointerDown(event));
        this.bind(this.stage, 'pointermove', (event) => this.onPointerMove(event));
        this.bind(this.stage, 'pointerup', (event) => this.onPointerUp(event));
        this.bind(this.stage, 'pointercancel', () => this.resetPointer());
        this.bind(this.root, 'pointermove', () => this.showInterface());
        this.bind(this.root, 'touchstart', () => this.showInterface(), { passive: true });
        this.bind(document, 'fullscreenchange', () => this.syncFullscreen());
        this.bind(document, 'shopify:block:select', (event) => this.onBlockSelect(event));

        this.update(true);
        this.showInterface();
        requestAnimationFrame(() => this.stage.focus({ preventScroll: true }));
      }

      buildAutoplayButton() {
        const controls = this.root.querySelector('.ttd-presentation__controls');
        if (!controls) return null;

        const existing = controls.querySelector('[data-presentation-autoplay]');
        if (existing) return existing;

        const divider = controls.querySelector('.ttd-presentation__control-divider');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ttd-control ttd-control--autoplay';
        button.dataset.presentationAutoplay = '';
        button.setAttribute('aria-label', 'Start autoplay');
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML = `
          <svg class="ttd-icon-autoplay" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8.5 7.3 16 12l-7.5 4.7V7.3Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7"/>
            <path d="M18.6 7.2A8 8 0 1 1 12 4" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5"/>
            <path d="m15.9 4 2.9.1-.2 2.9" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
          </svg>
          <span class="ttd-control__autoplay-label">AUTO</span>
        `;

        if (divider) {
          divider.insertAdjacentElement('afterend', button);
        } else {
          controls.appendChild(button);
        }

        return button;
      }

      bind(target, eventName, handler, options) {
        if (!target) return;
        target.addEventListener(eventName, handler, options);
        this.listeners.push([target, eventName, handler, options]);
      }

      goTo(nextIndex, immediate = false) {
        const boundedIndex = Math.max(0, Math.min(nextIndex, this.slides.length - 1));
        if (boundedIndex === this.index && !immediate) return;
        if (this.locked && !immediate) return;

        this.index = boundedIndex;
        this.update(immediate);

        if (this.autoplaying) this.scheduleAutoplay();

        if (!immediate) {
          this.locked = true;
          window.setTimeout(() => { this.locked = false; }, 700);
        }
      }

      update(immediate = false) {
        if (immediate) this.root.classList.add('is-instant');

        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.classList.toggle('is-active', active);
          slide.classList.toggle('is-before', slideIndex < this.index);
          slide.classList.toggle('is-after', slideIndex > this.index);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if ('inert' in slide) slide.inert = !active;
        });

        this.progressButtons.forEach((button, buttonIndex) => {
          const active = buttonIndex === this.index;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', active ? 'step' : 'false');
        });

        if (this.previousButton) this.previousButton.disabled = this.index === 0;
        if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;

        const activeSlide = this.slides[this.index];
        const slideNumber = String(this.index + 1).padStart(2, '0');
        const progressButton = this.progressButtons[this.index];
        const label = progressButton ? progressButton.getAttribute('aria-label').replace(/^.*?:\s*/, '') : '';
        if (this.currentNumber) this.currentNumber.textContent = slideNumber;
        if (this.currentLabel) this.currentLabel.textContent = label;
        if (this.status) this.status.textContent = `Chapter ${this.index + 1} of ${this.slides.length}. ${label}`;

        this.showInterface();
        activeSlide?.focus?.({ preventScroll: true });

        if (immediate) requestAnimationFrame(() => this.root.classList.remove('is-instant'));
      }

      onKeydown(event) {
        if (!document.body.classList.contains('ttd-presentation-mode')) return;
        const interactive = event.target.closest('a, button, input, textarea, select');
        const key = event.key;

        if (interactive && key !== 'Escape') return;

        if (['ArrowRight', 'ArrowDown', 'PageDown', 'Enter'].includes(key)) {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(key)) {
          event.preventDefault();
          this.goTo(this.index - 1);
        } else if (key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        } else if (key === 'End') {
          event.preventDefault();
          this.goTo(this.slides.length - 1);
        } else if (key === ' ' || key.toLowerCase() === 'p') {
          event.preventDefault();
          this.togglePause();
        } else if (key.toLowerCase() === 'a') {
          event.preventDefault();
          this.toggleAutoplay();
        } else if (key.toLowerCase() === 'f') {
          event.preventDefault();
          this.toggleFullscreen();
        } else if (key.toLowerCase() === 'r') {
          event.preventDefault();
          this.restart();
        }
      }

      onWheel(event) {
        if (event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        this.wheelTotal += Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        window.clearTimeout(this.wheelTimer);
        this.wheelTimer = window.setTimeout(() => { this.wheelTotal = 0; }, 180);

        if (Math.abs(this.wheelTotal) < 85) return;
        const direction = this.wheelTotal > 0 ? 1 : -1;
        this.wheelTotal = 0;
        this.goTo(this.index + direction);
      }

      onPointerDown(event) {
        if (event.target.closest('a, button')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.stage.setPointerCapture?.(event.pointerId);
      }

      onPointerMove(event) {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
        const x = event.clientX - this.pointerStart.x;
        const y = event.clientY - this.pointerStart.y;
        const distance = Math.max(Math.abs(x), Math.abs(y));
        this.root.style.setProperty('--drag-opacity', String(Math.min(distance / 500, .16)));
      }

      onPointerUp(event) {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
        const x = event.clientX - this.pointerStart.x;
        const y = event.clientY - this.pointerStart.y;
        const dominant = Math.abs(x) > Math.abs(y) ? x : y;
        if (Math.abs(dominant) > 54) this.goTo(this.index + (dominant < 0 ? 1 : -1));
        this.resetPointer();
      }

      resetPointer() {
        this.pointerStart = null;
        this.root.style.removeProperty('--drag-opacity');
      }

      togglePause() {
        this.paused = !this.paused;
        this.root.classList.toggle('is-paused', this.paused);
        this.pauseButton?.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton?.setAttribute('aria-label', this.paused ? 'Resume animations' : 'Pause animations');
        this.showInterface();
      }

      toggleAutoplay() {
        this.autoplaying = !this.autoplaying;
        this.root.classList.toggle('is-autoplaying', this.autoplaying);
        this.autoplayButton?.setAttribute('aria-pressed', String(this.autoplaying));
        this.autoplayButton?.setAttribute('aria-label', this.autoplaying ? 'Stop autoplay' : 'Start autoplay');

        if (this.autoplaying) {
          this.scheduleAutoplay();
        } else {
          this.stopAutoplayTimer();
        }

        this.showInterface();

        // Return focus to the presentation so the controls can auto-hide cleanly
        // during unattended event playback.
        requestAnimationFrame(() => {
          this.autoplayButton?.blur();
          this.stage?.focus?.({ preventScroll: true });
        });
      }

      scheduleAutoplay() {
        this.stopAutoplayTimer();
        if (!this.autoplaying || this.slides.length < 2) return;

        this.autoplayTimer = window.setTimeout(() => {
          const nextIndex = this.index >= this.slides.length - 1 ? 0 : this.index + 1;
          this.goTo(nextIndex);
        }, this.autoplayDelay);
      }

      stopAutoplayTimer() {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      restart() {
        this.paused = false;
        this.root.classList.remove('is-paused');
        this.pauseButton?.setAttribute('aria-pressed', 'false');
        this.goTo(0, true);
      }

      async toggleFullscreen() {
        try {
          if (!document.fullscreenElement) {
            await this.root.requestFullscreen?.({ navigationUI: 'hide' });
          } else {
            await document.exitFullscreen?.();
          }
        } catch (error) {
          if (this.status) this.status.textContent = 'Fullscreen mode is not available in this browser.';
        }
      }

      syncFullscreen() {
        this.root.classList.toggle('is-fullscreen', document.fullscreenElement === this.root);
      }

      showInterface() {
        this.root.classList.remove('is-ui-hidden');
        window.clearTimeout(this.uiTimer);
        this.uiTimer = window.setTimeout(() => {
          if (this.autoplaying || !this.root.matches(':focus-within')) {
            this.root.classList.add('is-ui-hidden');
          }
        }, 3600);
      }

      onBlockSelect(event) {
        const blockElement = event.target.closest('[data-presentation-slide]');
        if (!blockElement || !this.root.contains(blockElement)) return;
        this.goTo(Number(blockElement.dataset.slideIndex), true);
      }

      destroy() {
        this.listeners.forEach(([target, eventName, handler, options]) => target.removeEventListener(eventName, handler, options));
        window.clearTimeout(this.uiTimer);
        window.clearTimeout(this.wheelTimer);
        this.stopAutoplayTimer();
        document.body.classList.remove('ttd-presentation-mode');
      }
    }

    const instances = new Map();

    const mount = (scope = document) => {
      scope.querySelectorAll(selector).forEach((root) => {
        if (instances.has(root)) return;
        instances.set(root, new TTDPresentation(root));
      });
    };

    const unmount = (scope) => {
      scope.querySelectorAll?.(selector).forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });
    };

    mount();
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
    document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  })();